import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem as MuiMenuItem,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  Button,
  InputLabel,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  addMenuOptionToGroup,
  removeMenuOptionFromGroup,
  fetchMenuOptions,
  fetchMenuOptionGroups,
  fetchMenuOptionMenuOptionGroup,
} from '../../../Redux/MenuOption/menuOptionSlice';
import { MenuOption, MenuOptionGroup } from '../../../Redux/MenuOption/types';

interface MenuOptionAttachmentProps {
  storeId: string;
}

interface MenuOptionItemProps {
  option: MenuOption;
  onAttach?: (optionId: string) => Promise<void>;
  onDetach?: (optionId: string) => Promise<void>;
  isAttached: boolean;
}

const MenuOptionItemComponent: React.FC<MenuOptionItemProps> = ({
  option,
  onAttach,
  onDetach,
  isAttached,
}) => {
  return (
    <ListItem
      sx={{
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <ListItemText
        primary={option.name}
        secondary={option.price ? `¥${option.price}` : undefined}
      />
      <Button
        onClick={() => isAttached ? onDetach?.(option.id) : onAttach?.(option.id)}
        startIcon={isAttached ? <LinkOffIcon /> : <LinkIcon />}
      >
        {isAttached ? '解除' : '紐付け'}
      </Button>
    </ListItem>
  );
};

const MenuOptionAttachment: React.FC<MenuOptionAttachmentProps> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [toggleView, setToggleView] = useState<'attached' | 'unattached'>('attached');
  const [error, setError] = useState<string | null>(null);

  const menuOptions = useSelector((state: RootState) => state.menuOption.menuOptions) || [];
  const menuOptionGroups = useSelector((state: RootState) => state.menuOption.menuOptionGroups) || [];
  const menuOptionMenuOptionGroup = useSelector((state: RootState) => state.menuOption.menuOptionMenuOptionGroup) || [];
  const loading = useSelector((state: RootState) => state.menuOption.loading);

  const filteredOptions = useMemo(() => {
    if (!selectedGroup || !Array.isArray(menuOptions) || !Array.isArray(menuOptionMenuOptionGroup)) return [];
    
    const attachedOptionIds = new Set(
      menuOptionMenuOptionGroup
        .filter(group => group && group.menuOptionGroupId === selectedGroup)
        .map(group => group.menuOptionId)
    );
    
    return menuOptions
      .filter(option => option && option.id)
      .filter(option => 
        toggleView === 'attached' ? attachedOptionIds.has(option.id) : !attachedOptionIds.has(option.id)
      );
  }, [menuOptions, menuOptionMenuOptionGroup, selectedGroup, toggleView]);

  useEffect(() => {
    if (storeId) {
      dispatch(fetchMenuOptions(storeId)).catch((error: Error) => setError(error.message));
      dispatch(fetchMenuOptionGroups({ storeId, menuId: 'default' })).catch((error: Error) =>
        setError(error.message)
      );
    }
  }, [dispatch, storeId]);

  useEffect(() => {
    if (storeId && selectedGroup) {
      dispatch(fetchMenuOptionMenuOptionGroup({ storeId, groupId: selectedGroup}))
        .catch((error: Error) => setError(error.message));
    }
  }, [dispatch, storeId, selectedGroup]);

  const handleAttachOption = async (optionId: string) => {
    if (!selectedGroup) {
      setError('グループを選択してください');
      return;
    }

    try {
      await dispatch(
        addMenuOptionToGroup({
          storeId,
          groupId: selectedGroup,
          optionId,
          sortIndex: filteredOptions.length,
        })
      ).unwrap();
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleDetachOption = async (optionId: string) => {
    if (!selectedGroup) {
      setError('グループを選択してください');
      return;
    }

    try {
      await dispatch(
        removeMenuOptionFromGroup({
          storeId,
          groupId: selectedGroup,
          optionId,
        })
      ).unwrap();
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>オプショングループ</InputLabel>
          <Select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            label="オプショングループ"
          >
            {Array.isArray(menuOptionGroups) && menuOptionGroups.map((group) => (
              group && group.id && (
                <MuiMenuItem key={group.id} value={group.id}>
                  {group.name || '名称未設定'}
                </MuiMenuItem>
              )
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={toggleView}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setToggleView(newValue);
            }
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="attached">
            紐付け済み
          </ToggleButton>
          <ToggleButton value="unattached">
            未紐付け
          </ToggleButton>
        </ToggleButtonGroup>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      <List>
        {Array.isArray(filteredOptions) && filteredOptions.map((option) => (
          option && option.id && (
            <MenuOptionItemComponent
              key={option.id}
              option={option}
              onAttach={handleAttachOption}
              onDetach={handleDetachOption}
              isAttached={toggleView === 'attached'}
            />
          )
        ))}
      </List>
    </Box>
  );
};

export default MenuOptionAttachment;
